import { memo, useContext } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { SegmentChatContext } from '@trinity/chat-components/components/BrandInsights/SegmentChat.Context';
import { Lightbulb } from '@trinity/chat-components/components/Chat.Widget/Lightbulb';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import { Constants } from '@trinity/chat-components/components/Theme';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import AuraLogo from '@trinity/chat-components/static/images/aura-icon.svg?url';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';
import { UseAuraSystemAuthorIconContext } from './SystemAuthorIcon.context';

type UserAuthorProps = {
  author: Chat.Author | Chat.Creator;
  className?: string;
};

type SystemAuthorProps = {
  animate?: boolean;
  className?: string;
};

export const SystemAuthor = memo(({ animate, className }: SystemAuthorProps) => {
  const { isDigitalTwin, segmentIconUrl: dtSegmentIconUrl } = useAvatarPanel();
  const { segmentChatStatus, segmentIconUrl: ctxSegmentIconUrl } = useContext(SegmentChatContext);
  const isDigitalTwinsApp = useContext(UseAuraSystemAuthorIconContext);
  const segmentIconUrl = dtSegmentIconUrl ?? ctxSegmentIconUrl;
  const theming = useContext(AppThemingContext);
  const bubbleColor = theming?.enabled ? theming.theme.color.bubble || undefined : 'var(--pri-02)';

  return (
    <SystemAuthorRoot
      className={className}
      bubbleColor={bubbleColor}
      noPadding={(isDigitalTwin && !!segmentIconUrl) || (!isDigitalTwin && !!ctxSegmentIconUrl)}
      segmentMode={(segmentChatStatus || isDigitalTwinsApp) && !isDigitalTwin && !ctxSegmentIconUrl}
    >
      {(() => {
        if (isDigitalTwin) {
          return segmentIconUrl ? (
            <SegmentIcon src={segmentIconUrl} alt="Segment avatar" />
          ) : (
            <AuraIcon src={AuraLogo} alt="" aria-hidden="true" />
          );
        }
        if (segmentChatStatus || isDigitalTwinsApp) {
          return ctxSegmentIconUrl ? (
            <SegmentIcon src={ctxSegmentIconUrl} alt="Segment avatar" />
          ) : (
            <AuraIcon src={AuraLogo} alt="" aria-hidden="true" />
          );
        }
        return <Lightbulb size={22} color={bubbleColor} />;
      })()}
    </SystemAuthorRoot>
  );
});

export const UserAuthor = memo(({ author, className }: UserAuthorProps) => {
  return (
    <UserAuthorRoot className={className}>
      {getFirstLetter(author.profile.firstName)} {getFirstLetter(author.profile.lastName)}
    </UserAuthorRoot>
  );
});

function getFirstLetter(name: string) {
  if (!name) return '';

  return name.trim().at(0)?.toUpperCase() ?? '';
}

const AuthorRoot = styled('div')`
  border-radius: 50%;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UserAuthorRoot = styled(AuthorRoot)(
  ({ theme }) => `
  background-color: ${theme.palette.gray.light1};
  color: ${theme.palette.black.main};
  font-size: clamp(12px, 3.6cqi, 18px);
  font-family: ${theme.fonts.semiBold};
  letter-spacing: -2px;
`
);

const SystemAuthorRoot = styled(AuthorRoot, {
  shouldForwardProp: shouldForwardProp('noPadding', 'segmentMode', 'bubbleColor'),
})<{ noPadding?: boolean; segmentMode?: boolean; bubbleColor?: string }>(
  ({ theme, noPadding, segmentMode, bubbleColor }) => ({
    background: (() => {
      if (noPadding) return 'transparent';
      if (segmentMode) return 'linear-gradient(90deg, #4F35E6 0%, #28AAE1 100%)';
      return bubbleColor ?? theme.palette.primary.light6;
    })(),
    ...(noPadding && { padding: 0 }),
    // Match the Aura button in the left nav (Sidebar `SegmentsButtonCollapsed`): same size, gradient and shadows.
    // `&&` outranks the width/height from the message `author` css passed in via className.
    ...(segmentMode && {
      '&&': {
        width: Constants.Sidebar.IconSize,
        height: Constants.Sidebar.IconSize,
      },
      filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7))',
      boxShadow: 'inset 0 1px 3px 0 #1a113b',
    }),
    '& > svg': {
      width: '55%',
      height: 'auto',
    },
  })
);

const SegmentIcon = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;

const AuraIcon = styled.img`
  width: 25px;
  height: auto;
  display: block;
  margin-left: 1px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
`;
